<template>
    <div class="container mt-4 mb-5">
        <div v-if="loading" class="text-center my-5">
            <div class="spinner-border"></div>
        </div>
        <div v-else>
            <div class="row">
                <!-- Left layout: status summary -->
                <div class="col-md-4">
                    <div class="card hoverClick mb-4">
                        <div class="card-body text-center" @click="statusFilter = 1; fetchRepairs(1); scrollTable()">
                            <div class="circle-emoji mb-3">
                                <span>🚀</span>
                            </div>
                            <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow"
                                style="line-height: 3rem;">{{ repairTotals.opened }}</h1>
                            <p class="text-muted">Opened tickets</p>
                        </div>
                    </div>
                    <div class="card hoverClick mb-4">
                        <div class="card-body text-center" @click="statusFilter = 2; fetchRepairs(1); scrollTable()">
                            <div class="circle-emoji mb-3">
                                <span>🧰</span>
                            </div>
                            <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow"
                                style="line-height: 3rem;">{{ repairTotals.in_repair }}</h1>
                            <p class="text-muted">In repair tickets</p>
                        </div>
                    </div>
                    <div class="card hoverClick mb-4">
                        <div class="card-body text-center" @click="statusFilter = 3; fetchRepairs(1); scrollTable()">
                            <div class="circle-emoji mb-3">
                                <span>✅</span>
                            </div>
                            <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow"
                                style="line-height: 3rem;">{{ repairTotals.closed }}</h1>
                            <p class="text-muted">Closed tickets</p>
                        </div>
                    </div>
                    <div class="card mb-4">
                        <div class="card-body text-center">
                            <div class="d-flex justify-content-center">
                                <button
                                    class="btn btn-primary mb-3 rounded-circle d-flex align-items-center justify-content-center"
                                    style="width:86px; height:86px; font-size:0.8rem; padding:0; font-weight: 300 !important;">
                                    <span class="material-icons text-white"
                                        style="font-size:2.5rem;">qr_code_scanner</span>
                                </button>
                            </div>
                            <p class="text-muted">Click to open scan and see info from mobile on desktop</p>
                        </div>
                    </div>
                </div>
                <!-- Right: ticket and details in one card -->
                <div class="col-md-8">
                    <div class="card mb-4">
                        <div class="card-body">
                            <h5 class="card-title fw-bold mb-4" v-if="ticket && Object.keys(ticket).length">🎫 Ticket
                                #{{ ticket.ticketId }}</h5>
                            <!-- Ticket info as numbered list -->
                            <ol class="list-group mb-4" v-if="ticket && Object.keys(ticket).length">
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Job Name</p>
                                    </div>
                                    <p class="text-muted">{{ ticket.jobName}}</p>
                                </li>
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Company</p>
                                    </div>
                                    <p class="text-muted">{{ ticket.companyName }}</p>
                                </li>
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Serial Number</p>
                                    </div>
                                    <p class="text-muted">{{ ticket.sn }}</p>
                                </li>
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Status</p>
                                    </div>
                                    <p class="text-muted">
                                        <template v-if="ticket.status==1">
                                            🚀 opened
                                        </template>
                                        <template v-else-if="ticket.status==2">
                                            🧰 in repair
                                        </template>
                                        <template v-else-if="ticket.status==3">
                                            ✅ closed
                                        </template>
                                        <template v-else>
                                            Unknown
                                        </template>
                                    </p>
                                </li>
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Sent Date</p>
                                    </div>
                                    <p class="text-muted">{{ ticket.sentDate ? ticket.sentDate.substring(0,
                                        16).replace('T', ' ') : '' }}</p>
                                </li>
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Received Date</p>
                                    </div>
                                    <p class="text-muted">{{ ticket.receivedDate ? ticket.receivedDate.substring(0,
                                        16).replace('T', ' ') : '' }}</p>
                                </li>
                                <li class="list-group-item d-flex justify-content-between align-items-start">
                                    <div class="ms-2 me-auto">
                                        <p class="fw-bold">Notes</p>
                                    </div>
                                    <p class="text-muted">{{ ticket.sentNotes }}</p>
                                </li>
                            </ol>
                            <!-- Ticket details table -->
                            <div class="filter-row mb-5" v-if="ticket && Object.keys(ticket).length">
                                <h6 class="fw-bold mb-2">Ticket Details</h6>
                                <div v-if="details.length">
                                    <div class="table-responsive">
                                        <table class="table table-sm table-striped align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Description</th>
                                                    <th>PO</th>
                                                    <th>User</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="detail in details" :key="detail.id">
                                                    <td>{{ new Date(detail.eventDate).toLocaleString() }}</td>
                                                    <td>{{ detail.details }}</td>
                                                    <td>{{ detail.po }}</td>
                                                    <td>{{ detail.user }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <p class="text-muted" v-else>
                                    There are no details registered for this ticket.
                                </p>
                            </div>
                            <!-- Repair Registration Form -->
                            <h5>Repair Registration Form</h5>
                            <form @submit.prevent="registerRepair" class="mt-4">
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <label class="form-label">Date of Work Order</label>
                                        <input type="datetime-local" v-model="repairForm.workDate" class="form-control"
                                            required />
                                    </div>
                                    <div class="col-md-12">
                                        <label class="form-label">Tech/Employee</label>
                                        <multiselect v-model="repairForm.tech" :options="techStaff" :searchable="true"
                                            :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                                            placeholder="Select or search tech/employee" label="displayName"
                                            track-by="uid" :allow-empty="false" required>
                                            <template #option="props">
                                                <span v-if="props.option.displayName || props.option.name">
                                                    {{ props.option.displayName || props.option.name }}
                                                    <span v-if="props.option.email" class="text-muted"
                                                        style="font-size:0.9em;">
                                                        ({{ props.option.email }})
                                                    </span>
                                                </span>
                                            </template>
                                            <template #singleLabel="props">
                                                <span v-if="props.option.displayName || props.option.name">
                                                    {{ props.option.displayName || props.option.name }}
                                                    <span v-if="props.option.email" class="text-muted"
                                                        style="font-size:0.9em;">
                                                        ({{ props.option.email }})
                                                    </span>
                                                </span>
                                            </template>
                                        </multiselect>
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">Job Name</label>
                                        <input type="text" v-model="repairForm.jobName" class="form-control" required />
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">PO #</label>
                                        <input type="text" v-model="repairForm.po" class="form-control" required />
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">Contact Name</label>
                                        <input type="text" v-model="repairForm.contactName" class="form-control"
                                            required />
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">Contact #</label>
                                        <input type="text" v-mask="'###-###-####'" v-model="repairForm.contactNumber"
                                            class="form-control" required />
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">Unit Description</label>
                                        <select v-model="repairForm.unitDesc" class="form-control form-control-sm"
                                            required>
                                            <option disabled value="">Select a value</option>
                                            <option value="12 Station M DC">12 Station M DC</option>
                                            <option value="24 Station M DC">24 Station M DC</option>
                                            <option value="12 Station M AC">12 Station M AC</option>
                                            <option value="24 Station M AC">24 Station M AC</option>
                                            <option value="ACE">ACE</option>
                                            <option value="Piccolo 1/1">Piccolo 1/1</option>
                                            <option value="Piccolo 2/2">Piccolo 2/2</option>
                                            <option value="Piccolo 4/4">Piccolo 4/4</option>
                                            <option value="XPR5350e">XPR5350e</option>
                                            <option value="CP200">CP200</option>
                                            <option value="n/a">n/a</option>
                                        </select>

                                    </div>
                                    <div class="col-md-6 d-flex align-items-center">
                                        <div class="form-check mt-4">
                                            <input class="form-check-input" type="checkbox"
                                                v-model="repairForm.analogBoard" :true-value="1" :false-value="0"
                                                id="analogBoardCheck">
                                            <label class="form-check-label" for="analogBoardCheck">Analog Board</label>
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <label class="form-label">Issue Reported</label>
                                        <textarea v-model="repairForm.issueReported" class="form-control" rows="3"
                                            maxlength="200" required></textarea>
                                    </div>
                                    <div class="col-12">
                                        <label class="form-label">Dates</label>
                                        <div class="row g-2 align-items-end">
                                            <div class="col-md-5">
                                                <input type="datetime-local" v-model="dateInput.startTime"
                                                    class="form-control" placeholder="Start Time" />
                                            </div>
                                            <div class="col-md-5">
                                                <input type="datetime-local" v-model="dateInput.stopTime"
                                                    class="form-control" placeholder="Stop Time" />
                                            </div>
                                            <div class="col-md-2">
                                                <button type="button" class="btn btn-outline-primary btn-sm w-100"
                                                    @click="addDate">Add Date</button>
                                            </div>
                                        </div>
                                        <ul class="list-group mt-2" v-if="repairForm.dates.length">
                                            <li class="list-group-item d-flex justify-content-between align-items-center"
                                                v-for="(d, idx) in repairForm.dates" :key="idx">
                                                <p>{{ new Date(d.startTime).toLocaleString() }} - {{ new
                                                    Date(d.stopTime).toLocaleString() }}</p>
                                                <button type="button" class="btn btn-sm btn-danger"
                                                    @click="removeDate(idx)">Remove</button>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="col-12">
                                        <label class="form-label">Repair status</label>
                                        <div>
                                            <div class="form-check form-check-inline">
                                                <input class="form-check-input" type="radio" v-model="repairForm.status"
                                                    :value="1" id="statusOpened">
                                                <label class="form-check-label" for="statusOpened">Opened</label>
                                            </div>
                                            <div class="form-check form-check-inline">
                                                <input class="form-check-input" type="radio" v-model="repairForm.status"
                                                    :value="2" id="statusInRepair">
                                                <label class="form-check-label" for="statusInRepair">In repair</label>
                                            </div>
                                            <div class="form-check form-check-inline">
                                                <input class="form-check-input" type="radio" v-model="repairForm.status"
                                                    :value="3" id="statusClosed">
                                                <label class="form-check-label" for="statusClosed">Closed</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <label class="form-label">Description of Work Done</label>
                                        <textarea v-model="repairForm.description" class="form-control" rows="6"
                                            maxlength="1000"></textarea>
                                    </div>
                                    <div class="col-12 mt-3" v-if="ticket && Object.keys(ticket).length">
                                        <button type="submit" class="btn btn-primary w-100">Register Repair</button>
                                    </div>
                                    <div class="col-12 mt-3" v-else>
                                        <p>
                                            You must register and select a Serial Number from 'Inventory Screen'. You
                                            can click <router-link to="/dashboard/inventory">here</router-link> to go
                                            there.
                                        </p>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    <!-- Repairs table -->
                    <div class="card mt-5" ref="mainTable">
                        <div class="card-body">
                            <h5 class="card-title mb-3">🔧 Repairs List</h5>

                            <div class="row mb-3 mt-3 filter-row">
                                <div class="col-md-12">
                                    <p class="fw-bold">Filter</p>
                                </div>
                                <div class="col-md-12">
                                    <label>Job Name</label>
                                    <multiselect v-model="jobNameFilter" :options="jobNameFilterOptions"
                                        :searchable="true" :loading="jobNameFilterIsLoading" :internal-search="false"
                                        :clear-on-select="true" :close-on-select="true" :options-limit="10"
                                        :min-chars="2" label="jobNameLabel" track-by="id"
                                        placeholder="Search job name in tickets..."
                                        @search-change="asyncFindJobNameRepairs" @select="fetchRepairs" />

                                    <label class="mt-3">Serial (SN)</label>
                                    <multiselect v-model="snFilter" :options="snFilterOptions" :searchable="true"
                                        :loading="snFilterIsLoading" :internal-search="false" :clear-on-select="true"
                                        :close-on-select="true" :options-limit="10" :min-chars="2" label="searchName"
                                        track-by="ticketId" placeholder="Search S/N in repairs..."
                                        @search-change="asyncFindSnRepairs" @select="fetchRepairs" />

                                    <label class="form-label mt-3">Tech/Employee</label>
                                    <multiselect v-model="techFilter" :options="techStaff" :searchable="true"
                                        :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                                        placeholder="Select or search tech/employee" label="displayName" track-by="uid"
                                        :allow-empty="false" @select="fetchRepairs">
                                    </multiselect>

                                    <label class="mt-3">PO</label>
                                    <multiselect v-model="poFilter" :options="poFilterOptions" :searchable="true"
                                        :loading="poFilterIsLoading" :internal-search="false" :clear-on-select="true"
                                        :close-on-select="true" :options-limit="10" :min-chars="2" label="searchName"
                                        track-by="ticketId" placeholder="Search PO in repairs..."
                                        @search-change="asyncFindPoRepairs" @select="fetchRepairs" />



                                    <p class="text-muted mt-2">
                                        Type any value in the filters above and press <strong>Enter</strong> to search
                                        and
                                        narrow down the inventory tickets.
                                    </p>
                                </div>
                                <div class="col-md-6" ref="mainTable">
                                    <div class="btn-group w-100 mb-3" role="group" aria-label="Status filter">
                                        <button type="button" class="btn"
                                            :class="statusFilter === 0 ? 'btn-primary' : 'btn-outline-dark'"
                                            @click="statusFilter = 0; fetchRepairs(1)">
                                            All
                                        </button>
                                        <button type="button" class="btn"
                                            :class="{'btn-primary': statusFilter === 1, 'btn-outline-dark': statusFilter !== 1}"
                                            @click="statusFilter = 1; fetchRepairs(1);">
                                            🚀 Opened
                                        </button>
                                        <button type="button" class="btn"
                                            :class="{'btn-primary': statusFilter === 2, 'btn-outline-dark': statusFilter !== 2}"
                                            @click="statusFilter = 2; fetchRepairs(1)">
                                            🧰 In Repair
                                        </button>
                                        <button type="button" class="btn"
                                            :class="{'btn-primary': statusFilter === 3, 'btn-outline-dark': statusFilter !== 3}"
                                            @click="statusFilter = 3; fetchRepairs(1)">
                                            ✅ Closed
                                        </button>
                                    </div>
                                </div>
                                <div class="col-md-3">

                                </div>
                                <div class="col-md-3"></div>
                                <div class="col-md-12 text-center mt-5">
                                    <p class="serial-number" @click="clearAll">🔄 Reset filters</p>
                                </div>
                            </div>


                            <div class="d-flex justify-content-end mt-3">
                                <v-pagination v-model.number="repairsCurrentPage" :page-count="repairsTotalPages"
                                    @input="fetchRepairs" />
                            </div>
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <p>Total records: {{ repairsTotalRows }} </p>
                                </div>
                                <div>
                                    <p>Pages: {{ repairsTotalPages }}</p>
                                </div>
                            </div>
                            <vs-table>
                                <template #thead>
                                    <vs-tr>
                                        <vs-th>Repair ID</vs-th>
                                        <vs-th>S/N</vs-th>
                                        <vs-th>Date Work Order</vs-th>
                                        <vs-th>Tech</vs-th>
                                        <vs-th>Job</vs-th>
                                        <vs-th>PO</vs-th>
                                        <vs-th>Contact</vs-th>
                                        <vs-th>Contact #</vs-th>
                                        <vs-th>Status</vs-th>
                                        <vs-th style="width: 120px;">Actions</vs-th>
                                    </vs-tr>
                                </template>
                                <template #tbody>
                                    <vs-tr v-for="(repair, idx) in repairs" :key="repair.id">
                                        <vs-td>
                                            <p class="serial-number mb-2" @click.prevent="viewRepair(repair)">#{{
                                                repair.id }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="serial-number mb-2" @click.prevent="viewRepair(repair)">{{
                                                repair.sn }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="mb-2">{{ repair.workDate ?
                                                repair.workDate.substring(0,16).replace('T',' ') : '' }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="mb-2" :title="repair.tech">
                                                {{
                                                repair.tech
                                                ? repair.tech.replace(/@.*/, '...')
                                                : ''
                                                }}
                                            </p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="mb-2">{{ repair.jobName }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="mb-2">{{ repair.po }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="mb-2">{{ repair.contactName }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <p class="mb-2">{{ repair.contactNumber }}</p>
                                        </vs-td>
                                        <vs-td>
                                            <template v-if="repair.status==1">
                                                <p class="mb-2">🚀 opened</p>
                                            </template>
                                            <template v-else-if="repair.status==2">
                                                <p class="mb-2">🧰 in repair</p>
                                            </template>
                                            <template v-else-if="repair.status==3">
                                                <p class="mb-2">✅ closed</p>
                                            </template>
                                            <template v-else>
                                                <p class="mb-2">Unknown</p>
                                            </template>
                                        </vs-td>
                                        <vs-td class="actions-col">
                                            <p class="serial-number me-3 mb-2" @click.prevent="printRepair(repair)">🖨️
                                                Print</p>
                                            <p class="serial-number" @click.prevent="deleteConfirmModal(repair)">❌
                                                Delete</p>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                            <div class="d-flex justify-content-between align-items-center mb-2 mt-3">
                                <div>
                                    <p>Total records: {{ repairsTotalRows }} </p>
                                </div>
                                <div>
                                    <p>Pages: {{ repairsTotalPages }}</p>
                                </div>
                            </div>
                            <div class="d-flex justify-content-center mt-3">
                                <v-pagination v-model.number="repairsCurrentPage" :page-count="repairsTotalPages"
                                    @input="fetchRepairs" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <div class="container-fluid" id="print-area" v-if="selectedRepair2">
            <div class="row">
                <div class="col-lg-12">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <div>
                            <img src="@/assets/logo-nav.png" width="180" />
                        </div>
                        <div class="text-center">
                            <img :src="qrData" alt="Código QR generado"
                                style="height: 150px; border-style: solid; border-width: 0.6rem;" />
                            <div class="fw-bold " style="font-size: 1.5rem;">Repair #{{ selectedRepair2.id }}</div>
                        </div>
                    </div>
                    <h5>Work Order</h5>
                    <table class="table table-bordered fSmall">
                        <tbody>
                            <tr>
                                <th>Serial Number</th>
                                <td>{{ selectedRepair2.sn }}</td>
                            </tr>
                            <tr>
                                <th>Date Work Order</th>
                                <td>{{ selectedRepair2.workDate ? new
                                    Date(selectedRepair2.workDate).toLocaleString() : '' }}</td>
                            </tr>
                            <tr>
                                <th>Job Name</th>
                                <td>{{ selectedRepair2.jobName }}</td>
                            </tr>
                            <tr>
                                <th>Tech</th>
                                <td>{{ selectedRepair2.tech }}</td>
                            </tr>

                            <tr>
                                <th>PO #</th>
                                <td>{{ selectedRepair2.po }}</td>
                            </tr>

                            <tr>
                                <th>Unit Description</th>
                                <td>{{ selectedRepair2.unitDesc }}</td>
                            </tr>
                            <tr>
                                <th>Analog Board</th>
                                <td>
                                    <template v-if="selectedRepair2.analogBoard == 1">Yes</template>
                                    <template v-else>No</template>
                                </td>
                            </tr>
                            <tr>
                                <th>Issue Reported</th>
                                <td>{{ selectedRepair2.issueReported }}</td>
                            </tr>
                            <tr>
                                <th>Description of Work Done</th>
                                <td>{{ selectedRepair2.description }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <h6 class="mt-4" v-if="selectedRepair2.history && selectedRepair2.history.length">Labor Time</h6>
                    <table class="table table-sm table-striped"
                        v-if="selectedRepair2.history && selectedRepair2.history.length">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(h, idx) in selectedRepair2.history" :key="h.id">
                                <td>{{ idx + 1 }}</td>
                                <td>{{ h.start ? new Date(h.start).toLocaleString() : '' }}</td>
                                <td>{{ h.end ? new Date(h.end).toLocaleString() : '' }}</td>
                                <td>
                                    <template v-if="h.start && h.end">
                                        {{
                                        (() => {
                                        const ms = new Date(h.end) - new Date(h.start);
                                        if (ms <= 0) return '0 min' ; const totalMinutes=Math.floor(ms / 60000); const
                                            days=Math.floor(totalMinutes / 1440); const hours=Math.floor((totalMinutes %
                                            1440) / 60); const minutes=totalMinutes % 60; let result='' ; if (days)
                                            result +=days + ' days ' ; if (hours) result +=hours + ' hours ' ; if
                                            (minutes || (!days && !hours)) result +=minutes + ' minutes' ; return
                                            result.trim(); })() }} </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <!-- Modal for RepairEditEvent -->
        <div class="modal fade" id="repairEditEventModal" tabindex="-1" aria-labelledby="repairEditEventModalLabel"
            aria-hidden="true" ref="repairEditEventModal" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="repairEditEventModalLabel">Edit Repair Event</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" @click="cleanComponent"
                            aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <RepairEditEvent v-if="selectedRepair" ref="repairEditEvent" :selectedRepair="selectedRepair"
                            @repairUpdated="fetchRepairs" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for Delete Confirmation -->
        <div class="modal fade" id="deleteConfirmModal" tabindex="-1" aria-labelledby="deleteConfirmModalLabel"
            aria-hidden="true" ref="deleteConfirmModal" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="deleteConfirmModalLabel">⚠️ Confirm Delete</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Are you sure you want to delete this repair?</p>
                        <p v-if="selectedRepair"><strong>Repair ID:</strong> #{{ selectedRepair.id }}</p>
                        <p v-if="selectedRepair"><strong>Serial:</strong> {{ selectedRepair.sn }}</p>
                        <p v-if="selectedRepair"><strong>Job Name:</strong> {{ selectedRepair.jobName }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-danger" @click="confirmDelete">Delete</button>
                    </div>
                </div>
            </div>
        </div>


        <!-- Modal for Print Confirmation -->
        <div class="modal fade" id="printModal" tabindex="-1" aria-labelledby="printModalLabel" aria-hidden="true"
            ref="printModal" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="printModalLabel">⚠️ Confirm Print</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">

                    </div>
                </div>
            </div>
        </div>

    </div>

</template>

<script>

    import axios from "axios";
    import vPagination from 'vue-plain-pagination';
    var config = require("@/config/api.json");
    const API = config.api;
    import RepairEditEvent from "@/views/RepairEditEvent.vue";
    import printJS from 'print-js'
    import QRCode from 'qrcode'
    import Multiselect from 'vue-multiselect'

    export default {
        name: "Repairs",
        components: { vPagination, RepairEditEvent, Multiselect },
        data() {
            return {
                poFilter: null,
                poFilterOptions: [],
                poFilterIsLoading: false,
                techFilter: null,
                jobNameFilter: null,
                jobNameFilterOptions: [],
                jobNameFilterIsLoading: false,
                statusFilter: 0,
                selectedRepair2: null,
                qrData: null,
                fechaActual: new Date().toLocaleDateString(),
                selectedRepair: null,
                ticket: {},
                details: [],
                loading: true,
                repairTotals: {
                    opened: 0,
                    in_repair: 0,
                    closed: 0
                },
                repairForm: {
                    serialNumber: '',
                    workDate: '',
                    tech: '',
                    jobName: '',
                    po: '',
                    contactName: '',
                    contactNumber: '',
                    issueReported: '',
                    description: '',
                    unitDesc: '',
                    analogBoard: 0,
                    status: 1,
                    dates: []
                },
                dateInput: {
                    startTime: '',
                    stopTime: ''
                },
                // Para la tabla de repairs
                repairs: [],
                repairsTotalPages: 1,
                repairsTotalRows: 0,
                repairsCurrentPage: 1,
                repairsLoading: false,
                snFilter: null,
                snFilterOptions: [],
                snFilterIsLoading: false,
                techStaff: [],
                user: null
            };
        },
        async mounted() {
            const ticketId = this.$route.params.ticketId;
            this.loading = true;
            if (!ticketId) {
                this.loading = false;
            } else {
                this.ticketAndDetails(ticketId);
                this.loading = false;
            }
            await this.fetchTechStaff();
            await this.fetchRepairs(1);
            await this.fetchRepairTotals();

            try {
                const { auth } = await import("@/fb");
                const user = auth.currentUser;
                if (user) {
                    this.user = user;
                } else {
                    // Si no está disponible inmediatamente, esperar a onAuthStateChanged
                    const { onAuthStateChanged } = await import("firebase/auth");
                    await new Promise((resolve) => {
                        onAuthStateChanged(auth, (u) => {
                            if (u) {
                                this.user = u;
                                resolve();
                            }
                        });
                    });
                }
            } catch (e) {
                this.user = null;
            }

        },
        methods: {

            clearAll() {
                this.snFilter = null;
                this.jobNameFilter = null;
                this.statusFilter = 0;
                this.techFilter = null;
                this.poFilter = null;
                this.fetchRepairs(1);
            },

            async asyncFindSnRepairs(query) {
                if (!query || query.length < 2) {
                    this.snFilterOptions = []
                    return
                }
                this.snFilterIsLoading = true
                try {
                    const res = await axios.get(`${API}/search-sn-repairs`, { params: { sn: query } })
                    this.snFilterOptions = res.data
                } catch (e) {
                    this.notify('error', 'Error buscando serial numbers en repairs')
                } finally {
                    this.snFilterIsLoading = false
                }
            },

            asyncFindPoRepairs(query) {
                if (!query || query.length < 2) {
                    this.poFilterOptions = []
                    return
                }
                this.poFilterIsLoading = true
                axios.get(`${API}/search-po-repairs`, { params: { po: query } })
                    .then(res => {
                        this.poFilterOptions = res.data
                    })
                    .catch(e => {
                        this.notify('error', 'Error buscando PO en repairs')
                    })
                    .finally(() => {
                        this.poFilterIsLoading = false
                    })
            },

            async asyncFindJobNameRepairs(query) {
                if (!query || query.length < 2) {
                    this.jobNameFilterOptions = []
                    return
                }
                this.jobNameFilterIsLoading = true
                try {
                    const res = await axios.get(`${API}/search-job-repairs`, { params: { job: query } })
                    this.jobNameFilterOptions = res.data

                } catch (e) {
                    this.notify('error', 'Error searching job names in tickets')
                } finally {
                    this.jobNameFilterIsLoading = false
                }
            },
            scrollTable() {
                const tableRef = this.$refs.mainTable;
                // If it's a Vue component, get its root element
                const el = tableRef && tableRef.$el ? tableRef.$el : tableRef;
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth' });
                }
            },
            async fetchTechStaff() {
                try {
                    const res = await axios.get(`${API}/get-techStaff`);
                    // Puedes guardar la respuesta en una variable de data si lo necesitas, por ejemplo:
                    this.techStaff = res.data;
                } catch (error) {
                    this.notify('error', 'Error fetching tech staff');
                    return [];
                }
            },
            async createQr(id) {
                try {
                    const value = String(id); // 🔄 Asegura que sea string
                    this.qrData = await QRCode.toDataURL(value);
                } catch (err) {
                    console.error('Error generando QR:', err)
                }
            },
            async printRepair(repair) {
                this.selectedRepair2 = repair;
                await this.createQr(repair.id);
                await this.callLog(this.user.email, 'print', 'repairs', `Print repair with ID ${repair.id}`);
                // Espera a que Vue reactive el DOM antes de imprimir
                this.$nextTick(() => {
                    printJS({
                        printable: 'print-area',
                        type: 'html',
                        targetStyles: ['*'],
                        scanStyles: true
                    });
                    this.selectedRepair2 = null; // Limpiar después de imprimir
                });

            },
            deleteConfirmModal(repair) {
                this.selectedRepair = repair;
                // Show the Bootstrap modal
                const modal = this.$refs.deleteConfirmModal;
                if (modal) {
                    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
                        setTimeout(() => {
                            bsModal.show();
                        }, 222);
                    }
                }
            },
            async confirmDelete() {
                try {
                    await axios.delete(`${API}/delete-repair/${this.selectedRepair.id}`);
                    this.notify('success', 'Repair deleted successfully');
                    // Log the deletion
                    await this.callLog(this.user.email, 'delete', 'repairs', `Deleted repair with ID ${this.selectedRepair.id}`);
                    // Hide the modal after deletion
                    const modal = this.$refs.deleteConfirmModal;
                    if (modal && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
                        bsModal.hide();
                    }
                    this.fetchRepairs(this.repairsCurrentPage);
                } catch (error) {
                    this.notify('error', 'Error deleting repair');
                }
            },
            cleanComponent() {
                this.selectedRepair = null;
                if (this.$refs.repairEditEventModal && this.$refs.repairEditEventModal.$destroy) {
                    this.$refs.repairEditEventModal.$destroy();
                }
            },
            viewRepair(repair) {
                this.selectedRepair = repair;
                const modal = this.$refs.repairEditEventModal;
                if (modal) {
                    // Show the Bootstrap modal
                    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
                        setTimeout(() => {
                            bsModal.show();
                        }, 222);
                    }
                }
            },
            async ticketAndDetails(ticketId) {
                try {
                    const res = await axios.get(`${API}/ticket-and-details`, { params: { ticketId } });
                    this.ticket = res.data.ticket || {};
                    this.details = res.data.details || [];
                    await this.fetchRepairTotals();
                    // Pre-fill serialNumber and jobName if available
                    if (this.ticket.sn) this.repairForm.serialNumber = this.ticket.sn;
                    if (this.ticket.jobName) this.repairForm.jobName = this.ticket.jobName;
                    // Cargar repairs al iniciar, filtrando por serial si existe
                    if (this.ticket.sn) {
                        this.snFilter = this.ticket.sn;
                        await this.fetchRepairs(1);
                    } else {
                        await this.fetchRepairs(1);
                    }
                } catch (e) {
                    this.ticket = {};
                    this.details = [];
                    this.notify('error', 'Error loading ticket');
                } finally {
                    this.loading = false;
                }
            },
            async fetchRepairTotals() {
                try {
                    const res = await axios.get(`${API}/repair-totals`);
                    if (res.data) {
                        this.repairTotals = res.data;
                    }
                } catch (e) {
                    this.repairTotals = { opened: 0, in_repair: 0, closed: 0 };
                }
            },
            async fetchRepairs(page = 1) {
                this.repairsLoading = true;
                // Build query params for GET request
                const params = {
                    page
                };
                if (this.snFilter) {
                    params.sn = this.snFilter;
                }
                if (this.statusFilter !== 0) {
                    params.status = this.statusFilter;
                }
                if (this.jobNameFilter) {
                    params.jobName = this.jobNameFilter.jobNameLabel;
                }
                if (this.snFilter) {
                    params.sn = this.snFilter.sn;
                }
                if (this.techFilter) {
                    params.tech = this.techFilter.email;
                }
                if (this.poFilter) {
                    params.po = this.poFilter.po;
                }
                // You can add more filters here if needed (jobName, po, status)
                try {
                    const res = await axios.get(`${API}/get-repairs-main`, { params });
                    if (res.data && Array.isArray(res.data.repairs)) {
                        this.repairs = res.data.repairs;
                        this.repairsTotalPages = res.data.totalPages;
                        this.repairsTotalRows = res.data.totalRows;
                        this.repairsCurrentPage = res.data.page;
                    } else {
                        this.repairs = [];
                        this.repairsTotalPages = 1;
                        this.repairsTotalRows = 0;
                        this.repairsCurrentPage = 1;
                    }
                } catch (e) {
                    this.repairs = [];
                    this.repairsTotalPages = 1;
                    this.repairsTotalRows = 0;
                    this.repairsCurrentPage = 1;
                    this.notify('error', 'Error loading repairs');
                } finally {
                    this.repairsLoading = false;
                }
            },
            onRepairsPageChange(page) {
                this.fetchRepairs(page);
            },
            async searchBySn() {
                this.repairsCurrentPage = 1;
                await this.fetchRepairs(1);
            },
            clearRepairsFilter() {
                this.snFilter = null;
                this.fetchRepairs(1);
            },
            addDate() {
                if (this.dateInput.startTime && this.dateInput.stopTime) {
                    if (new Date(this.dateInput.stopTime) < new Date(this.dateInput.startTime)) {
                        this.notify('error', 'Stop time cannot be before start time.');
                        return;
                    }
                    this.repairForm.dates.push({
                        startTime: this.dateInput.startTime,
                        stopTime: this.dateInput.stopTime
                    });
                    this.dateInput.startTime = '';
                    this.dateInput.stopTime = '';
                }
            },
            removeDate(idx) {
                this.repairForm.dates.splice(idx, 1);
            },
            async registerRepair() {
                try {
                    const payload = { ...this.repairForm };
                    if (payload.tech && typeof payload.tech === 'object') {
                        payload.tech = payload.tech.email || (this.repairForm.tech && this.repairForm.tech.email) || '--';
                    }
                    if (!payload.tech || payload.tech === '--') {
                        this.notify('error', 'You must select a Tech/Employee.');
                        return;
                    }
                    // Convert analogBoard to int
                    payload.analogBoard = payload.analogBoard ? 1 : 0;
                    const res = await axios.post(`${API}/create-repair`, payload);
                    if (res.data && res.data.message === 'success') {
                        this.notify('success', 'Repair registered successfully!');
                        // Optionally clear form or reload data
                        this.repairForm = {
                            serialNumber: this.ticket.sn || '',
                            workDate: '',
                            tech: '',
                            jobName: this.ticket.jobName || '',
                            po: '',
                            contactName: '',
                            contactNumber: '',
                            issueReported: '',
                            description: '',
                            unitDesc: '',
                            analogBoard: 0,
                            status: 1,
                            dates: []
                        };
                        // Refrescar repairs después de registrar uno nuevo
                        this.fetchRepairs(1);
                    } else {
                        throw new Error('Error registering repair');
                    }
                } catch (e) {
                    this.notify('error', 'Error registering repair');
                }
            }
        },
    };
</script>

<style>
    .fSmall {
        font-size: 8px !important;
    }

    .fSmall {
        font-size: 10px !important;
    }

    @media print {
        #print-area .fSmall {
            font-size: 3px !important;
        }

        body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        @page {
            size: A4 portrait;
            margin: 1cm;
        }
    }
</style>